// Detect whether the PWA is running inside a Trusted Web Activity (TWA)
// wrapper on Android — only then can we use the Digital Goods API to take
// Google Play Billing payments.
//
// Detection challenges:
//   * `document.referrer` is `android-app://<package>` only on the FIRST
//     page load. Client-side React-Router navigation then overwrites it,
//     so a one-shot check fails on subsequent pages (e.g. /app/subscribe
//     opened from Menu).
//   * `getDigitalGoodsService` is only exposed when Bubblewrap was built
//     with `features.playBilling.enabled: true`. It's the strongest single
//     signal for "billing is available", but we still want to identify
//     the TWA shell even if billing isn't wired (so we can show a clearer
//     "please update the app" message).
//
// Strategy:
//   1. On first reachable boot, snapshot `document.referrer` and the
//      `display-mode: standalone` media query.
//   2. Cache the answer in sessionStorage so client-side navigations keep
//      reporting the right value.
//   3. Expose two flags: `isRunningInTwa()` (any Android-app shell) and
//      `hasPlayBilling()` (DGAPI is actually callable).

const STORE_KEY = "vedari_runtime";

function readStore() {
  if (typeof sessionStorage === "undefined") return {};
  try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || "{}"); } catch { return {}; }
}

function writeStore(patch) {
  if (typeof sessionStorage === "undefined") return;
  try {
    const next = { ...readStore(), ...patch };
    sessionStorage.setItem(STORE_KEY, JSON.stringify(next));
  } catch {
    /* private mode etc. */
  }
}

// Call once at app boot so we capture document.referrer before any
// client-side navigation overwrites it.
export function snapshotRuntime() {
  if (typeof window === "undefined") return;
  const ref = (document.referrer || "");
  const fromAndroidApp = ref.startsWith("android-app://");
  const isStandalone = !!(window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
  const hasDgApi = typeof window.getDigitalGoodsService === "function";
  const ua = navigator.userAgent || "";
  const isAndroid = /Android/i.test(ua);
  // sticky TWA flag — once true, stay true for the session
  const prev = readStore();
  const inTwa = prev.in_twa || hasDgApi || (fromAndroidApp && isAndroid);
  writeStore({
    in_twa: !!inTwa,
    has_dgapi: hasDgApi,
    from_android_app: fromAndroidApp,
    standalone: isStandalone,
    is_android: isAndroid,
    referrer: ref,
  });
}

export function isRunningInTwa() {
  if (typeof window === "undefined") return false;
  // sessionStorage cache wins for stability across client-side nav
  const cached = readStore();
  if (cached.in_twa) return true;
  // Live fallback if snapshotRuntime wasn't called yet
  const hasDgApi = typeof window.getDigitalGoodsService === "function";
  const fromAndroidApp = (document.referrer || "").startsWith("android-app://");
  const isAndroid = /Android/i.test(navigator.userAgent || "");
  const result = hasDgApi || (fromAndroidApp && isAndroid);
  if (result) writeStore({ in_twa: true, has_dgapi: hasDgApi });
  return result;
}

// Stronger signal: can we actually call the Digital Goods API?
export function hasPlayBilling() {
  return typeof window !== "undefined" && typeof window.getDigitalGoodsService === "function";
}

// Read snapshot for diagnostics on the Paywall page.
export function getRuntimeDiagnostics() {
  return readStore();
}

// SKUs configured in Google Play Console → Monetize → Products → Subscriptions.
export const PLAY_SKUS = {
  monthly: "vedari_monthly",
  yearly: "vedari_yearly",
};
